import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { InAppContentDefinition, InAppDestination } from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { selectIsRokuTemplatingFeatureFlagOn } from 'core/userContext';
import { SegmentedControlOption } from 'ui/segmentedControl';
import { IN_APP_VIEW_OPTIONS } from '../../constants';
import { useInAppPreviewContext } from '../../context/InAppPreviewContext';

export const useGetViewOptions = () => {
  const { hasContent, liveTemplate } = useEditableFieldsContext();
  const { setPreviewViewOption } = useInAppPreviewContext();

  const isRokuTemplatingOn = useSelector(selectIsRokuTemplatingFeatureFlagOn);
  const inAppMediumData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;

  const { Mobile, Browser, Roku } = InAppDestination;
  const hasAppTarget = inAppMediumData?.destinations?.includes(Mobile);
  const hasBrowserTarget = inAppMediumData?.destinations?.includes(Browser);
  const hasRokuTarget = inAppMediumData?.destinations?.includes(Roku);
  const hasMobileInboxEnabled = inAppMediumData.saveToInbox;

  const formatOption = (name: string, value: IN_APP_VIEW_OPTIONS): SegmentedControlOption => ({
    name,
    key: value,
    value,
  });

  useEffect(() => {
    if (hasAppTarget || (!hasAppTarget && !hasBrowserTarget && !hasRokuTarget))
      setPreviewViewOption(IN_APP_VIEW_OPTIONS.inApp);
    if (hasRokuTarget && !hasAppTarget && !hasBrowserTarget)
      setPreviewViewOption(IN_APP_VIEW_OPTIONS.roku);
  }, [hasAppTarget, hasBrowserTarget, hasRokuTarget]);

  const viewOptions = useMemo(() => {
    const options = [];

    if (!inAppMediumData?.destinations || inAppMediumData?.destinations?.length === 0) {
      options.push(formatOption('App', IN_APP_VIEW_OPTIONS.inApp));
      options.push(formatOption('Web', IN_APP_VIEW_OPTIONS.web));
      if (hasMobileInboxEnabled) options.push(formatOption('Inbox', IN_APP_VIEW_OPTIONS.inbox));

      return options;
    }

    if (hasAppTarget) options.push(formatOption('App', IN_APP_VIEW_OPTIONS.inApp));

    if (hasBrowserTarget) options.push(formatOption('Web', IN_APP_VIEW_OPTIONS.web));

    if (isRokuTemplatingOn && hasRokuTarget)
      options.push(formatOption('Roku', IN_APP_VIEW_OPTIONS.roku));

    if (hasMobileInboxEnabled) options.push(formatOption('Inbox', IN_APP_VIEW_OPTIONS.inbox));

    return options;
  }, [hasContent, hasMobileInboxEnabled, inAppMediumData?.destinations]);

  return viewOptions;
};
